"use client";

import { Auction, AuctionItem } from "@/types/auction";
import FAQItem from "@/components/Elements/FAQItem";
import React, { useEffect, useState } from "react";

interface AuctionItemFAQsProps {
  auctionItem: AuctionItem;
  auction: Auction;
  themeSettings: any;
}

const TEXT_NO_INFORMATION_AVAILABLE = "Ingen information finns tillgänglig";

const AuctionItemFAQs: React.FC<AuctionItemFAQsProps> = ({
  auctionItem,
  auction,
  themeSettings,
}) => {
  const pages = themeSettings?.settingsRopFaq || {};

  const auctionAddress = auctionItem?.address
    ? auctionItem?.address
    : auction?.addresses
    ? auction?.addresses[0]
    : false;

  const [currentHash, setCurrentHash] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const getHashBang = (hash: string) => hash.split("#")[1];
      setCurrentHash(getHashBang(window.location.hash));
    }
  }, []);

  useEffect(() => {
    if (currentHash) {
      const element = document.getElementById(`faq-${currentHash}`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [currentHash]);

  const processIfStatements = (text: string, context: Record<string, any>) => {
    const regex = /\{if ([^}]+)\}([\s\S]*?)\{\/if\}/g; // Match {if ...}{/if} blocks
    return text.replace(regex, (_, condition, content) => {
      try {
        // Validate and safely create the dynamic function
        const fn = new Function(
          ...Object.keys(context),
          `return ${condition};`
        );
        return fn(...Object.values(context)) ? content : "";
      } catch (error) {
        //console.error(`Error evaluating condition: "${condition}"`, error);
        return ""; // Return empty if the condition is invalid
      }
    });
  };

  const getNestedValue = (obj: any, path: string): string => {
    const value = path.split(".").reduce((acc, key) => acc?.[key], obj);
    if (value === null || value === undefined) return "";
    if (typeof value === "object") {
      if ("text" in value) return String(value.text ?? "");
      return "";
    }
    return String(value);
  };

  const replaceVariables = (text: string) => {
    if (!text) return "";

    let replacedText = text;

    // Process {if ...}{/if} statements
    try {
      replacedText = processIfStatements(replacedText, {
        item: auctionItem,
        auction,
      });
    } catch (error) {
      console.error("Error processing if statements", error);
    }

    // Replace static placeholders
    replacedText = replacedText.replace(
      /\{auction\.([^}]+)\}/g,
      (_, p1) => getNestedValue(auction, p1)
    );

    replacedText = replacedText.replace(
      /\{item\.([^}]+)\}/g,
      (_, p1) => getNestedValue(auctionItem, p1)
    );

    replacedText = replacedText.replace(/\{path-([^}]+)\}/g, (_, p1) => {
      if (p1 === "emptyRegistrationTerms") {
        return themeSettings?.pages?.pageregistrationterms?.uri || "";
      }
      return "";
    });

    return replacedText;
  };

  const sections = {
    information: {
      textWrapper: () => {
        const data = pages["information"];
        let text = data?.text;

        if (!text) return TEXT_NO_INFORMATION_AVAILABLE;

        return replaceVariables(text);
      },
    },
    questions: {
      textWrapper: () => {
        const data = pages["questions"];
        let text = data?.text;

        if (!text) return TEXT_NO_INFORMATION_AVAILABLE;

        return replaceVariables(text);
      },
    },
    viewing: {
      textWrapper: () => {
        const data = pages["viewing"];
        let text = data?.text;

        if (!text) return TEXT_NO_INFORMATION_AVAILABLE;

        return replaceVariables(text);
      },
    },
    payment: {
      textWrapper: () => {
        const data = pages["payment"];
        let text = data?.text;

        if (!text) return TEXT_NO_INFORMATION_AVAILABLE;

        return replaceVariables(text);
      },
    },
    pickup: {
      textWrapper: () => {
        const data = pages["pickup"];
        let text = data?.text;

        if (!text) return TEXT_NO_INFORMATION_AVAILABLE;

        return replaceVariables(text);
      },
    },
    forklift: {
      textWrapper: () => {
        const data = pages["forklift"];
        let text = "";

        if (auctionAddress) {
          const forkLift = auctionAddress?.addressForkliftHelp;
          const loadingInfo = auctionAddress?.addressLoadingInfo;

          if (forkLift === "yes") {
            text += data.textYes;
          } else if (forkLift === "custom") {
            text += loadingInfo;
          } else if (forkLift === "no") {
            text += data.textNo;
          }
        } else {
          text = TEXT_NO_INFORMATION_AVAILABLE;
        }

        if (!text) return TEXT_NO_INFORMATION_AVAILABLE;

        return replaceVariables(text);
      },
    },
    freight: {
      textWrapper: () => {
        const data = pages["freight"];
        let text = "";

        if (auctionAddress) {
          const freight = auctionAddress.addressFreightHelp;
          const freightInfo = auctionAddress.addressFreightInfo;

          if (freightInfo) {
            text += freightInfo;
          } else if (freight === "yes") {
            text += data.textYes;
          } else {
            text += data.textNo;
          }
        } else {
          text = "Ingen information tillgänglig";
        }

        if (!text) return TEXT_NO_INFORMATION_AVAILABLE;

        return replaceVariables(text);
      },
    },
  };

  const cleanHtml = (html: string): string => {
    // html = html.replace(/<p>(\s|&nbsp;|<br>)*<\/p>/g, "");
    // html = html.replace(/<p>\s*<p>/g, "<p>");
    // html = html.replace(/<br\s*\/?>/g, "");
    // html = html.replace(/<\/p>\s*<\/p>/g, "</p>");

    return html;
  };

  return (
    <div className="flex flex-col space-y-4 md:space-y-4 w-full mt-8">
      {Object.entries(sections).map(([key, item]) => {
        const page = pages?.[key];

        if (!page) {
          return null;
        }

        const answer = cleanHtml(
          `<div className="font-normal text-16 tovek-auction-faq py-2">${item.textWrapper()}</div>`
        );

        return (
          <div id={`faq-${key}`} key={key} style={{ scrollMarginTop: "200px" }}>
            <FAQItem
              icon={page?.icon?.sourceUrl}
              openOnInit={currentHash === `faq-${key}`}
              attributes={{
                question: page?.title || `Information del ${item}`,
                answer: answer,
              }}
              bodyClass="text-18 !py-0"
            />
          </div>
        );
      })}
    </div>
  );
};

export default AuctionItemFAQs;
